/**
 * Draft Storage Utility
 * Manages local storage for email drafts with different key strategies
 */

import { logger } from "../../logger";

export interface EmailDraft {
  to: string;
  subject: string;
  body: string;
  fromAccount: string;
  templateId?: string;
  selectedClientId?: string;
  variableValues: Record<string, string>;
  lastSaved: number;
  type: "compose" | "reply";
  originalMessageId?: string; // For replies
}

const DRAFT_STORAGE_PREFIX = "email_draft_";
const DRAFT_LIST_KEY = "email_drafts_list";

/**
 * Generate storage key for drafts
 */
export function getDraftKey(type: "compose" | "reply", identifier: string): string {
  if (type === "compose") {
    // For compose: use recipient email as key
    return `${DRAFT_STORAGE_PREFIX}compose_${identifier}`;
  } else {
    // For reply: use original message ID as key
    return `${DRAFT_STORAGE_PREFIX}reply_${identifier}`;
  }
}

/**
 * Save draft to local storage
 */
export function saveDraft(
  type: "compose" | "reply",
  identifier: string,
  draft: Omit<EmailDraft, "type" | "lastSaved" | "originalMessageId">
): void {
  try {
    const key = getDraftKey(type, identifier);
    const fullDraft: EmailDraft = {
      ...draft,
      type,
      lastSaved: Date.now(),
      originalMessageId: type === "reply" ? identifier : undefined,
    };

    localStorage.setItem(key, JSON.stringify(fullDraft));

    // Update drafts list for easy enumeration
    updateDraftsList(key);
  } catch (error) {
    logger.warn("Failed to save draft", { error });
  }
}

/**
 * Load draft from local storage
 */
export function loadDraft(type: "compose" | "reply", identifier: string): EmailDraft | null {
  try {
    const key = getDraftKey(type, identifier);
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    return JSON.parse(stored) as EmailDraft;
  } catch (error) {
    logger.warn("Failed to load draft", { error });
    return null;
  }
}

/**
 * Delete draft from local storage
 */
export function deleteDraft(type: "compose" | "reply", identifier: string): void {
  try {
    const key = getDraftKey(type, identifier);
    localStorage.removeItem(key);

    // Update drafts list
    removeDraftFromList(key);
  } catch (error) {
    logger.warn("Failed to delete draft", { error });
  }
}

/**
 * Get all drafts
 */
export function getAllDrafts(): EmailDraft[] {
  try {
    const draftKeys = getDraftsList();
    const drafts: EmailDraft[] = [];

    for (const key of draftKeys) {
      const stored = localStorage.getItem(key);
      if (stored) {
        try {
          drafts.push(JSON.parse(stored) as EmailDraft);
        } catch (error) {
          logger.warn(`Failed to parse draft ${key}`, { key, error });
          // Remove corrupted draft
          localStorage.removeItem(key);
          removeDraftFromList(key);
        }
      }
    }

    // Sort by last saved (newest first)
    return drafts.sort((a, b) => b.lastSaved - a.lastSaved);
  } catch (error) {
    logger.warn("Failed to get all drafts", { error });
    return [];
  }
}

/**
 * Check if draft exists for identifier
 */
export function hasDraft(type: "compose" | "reply", identifier: string): boolean {
  const key = getDraftKey(type, identifier);
  return localStorage.getItem(key) !== null;
}

/**
 * Get drafts for specific email address (compose drafts)
 */
export function getDraftsForEmail(email: string): EmailDraft[] {
  return getAllDrafts().filter((draft) => draft.type === "compose" && draft.to === email);
}

/**
 * Get draft for specific message ID (reply drafts)
 */
export function getDraftForMessage(messageId: string): EmailDraft | null {
  return loadDraft("reply", messageId);
}

/**
 * Clean up old drafts (older than 30 days)
 */
export function cleanupOldDrafts(): void {
  try {
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const drafts = getAllDrafts();

    for (const draft of drafts) {
      if (draft.lastSaved < thirtyDaysAgo) {
        const identifier = draft.type === "compose" ? draft.to : draft.originalMessageId!;
        deleteDraft(draft.type, identifier);
      }
    }
  } catch (error) {
    logger.warn("Failed to cleanup old drafts", { error });
  }
}

/**
 * Private helper functions
 */
function getDraftsList(): string[] {
  try {
    const stored = localStorage.getItem(DRAFT_LIST_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    return [];
  }
}

function updateDraftsList(key: string): void {
  try {
    const list = getDraftsList();
    if (!list.includes(key)) {
      list.push(key);
      localStorage.setItem(DRAFT_LIST_KEY, JSON.stringify(list));
    }
  } catch (error) {
    logger.warn("Failed to update drafts list", { error });
  }
}

function removeDraftFromList(key: string): void {
  try {
    const list = getDraftsList();
    const filtered = list.filter((k) => k !== key);
    localStorage.setItem(DRAFT_LIST_KEY, JSON.stringify(filtered));
  } catch (error) {
    logger.warn("Failed to remove draft from list", { error });
  }
}

/**
 * Auto-save functionality with debouncing
 */
export class DraftAutoSaver {
  private timeout: NodeJS.Timeout | null = null;
  private readonly debounceMs: number;

  constructor(debounceMs: number = 2000) {
    this.debounceMs = debounceMs;
  }

  autoSave(
    type: "compose" | "reply",
    identifier: string,
    draft: Omit<EmailDraft, "type" | "lastSaved" | "originalMessageId">
  ): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => {
      saveDraft(type, identifier, draft);
    }, this.debounceMs);
  }

  forceSave(
    type: "compose" | "reply",
    identifier: string,
    draft: Omit<EmailDraft, "type" | "lastSaved" | "originalMessageId">
  ): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    saveDraft(type, identifier, draft);
  }

  cancel(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }
}
